import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useWindowSize } from './SiteContext';
// import * as AppPropTypes from '../lib/PropTypes';

const VideoContext = React.createContext(null);

export const useVideoContext = () => useContext(VideoContext);

export const useFloatingVideo = () => {
    const {
        floatingVideo,
        videoMetadata,
        setFloatingVideo,
        addFloatingVideoSlot,
        updateFloatingVideoSlot,
        removeFloatingVideoSlot,
        floatingVideoSlots,
        enableVideoSlotByName,
        disableVideoSlotByName,
        disableFixedSlot,
        enableFixedSlot,
        fixedSlot,
        maxVideoWidth,
        fullscreen,
        setFullscreen,
    } = useVideoContext();

    return {
        floatingVideo,
        videoMetadata,
        setFloatingVideo,
        addFloatingVideoSlot,
        updateFloatingVideoSlot,
        removeFloatingVideoSlot,
        floatingVideoSlots,
        enableVideoSlotByName,
        disableVideoSlotByName,
        disableFixedSlot,
        enableFixedSlot,
        fixedSlot,
        maxVideoWidth,
        fullscreen,
        setFullscreen,
    };
};

const propTypes = {
    children: PropTypes.node.isRequired,
};

const defaultProps = {};

export const VideoProvider = ({ children }) => {
    const { width: windowWidth } = useWindowSize();
    // Floating video
    const [maxVideoWidth, setMaxVideoWidth] = useState(null);
    const [floatingVideo, setFloatingVideoItem] = useState(null);
    const [videoMetadata, setVideoMetadata] = useState(null);

    const setFloatingVideo = useCallback(
        (video, metadata = null) => {
            setFloatingVideoItem(video);
            setVideoMetadata(metadata);
        },
        [setFloatingVideoItem, setVideoMetadata],
    );

    const [floatingVideoSlots, setFloatingVideoSlots] = useState([]);
    const [fullscreen, setFullscreen] = useState(false);

    const addFloatingVideoSlot = useCallback(
        (id = null, name = null, ref = null, fallback = false, fixed = false) => {
            setFloatingVideoSlots((slots) => {
                const newSlots = [...slots].filter(({ id: slotId }) => slotId !== id);
                newSlots.push({ id, name, ref, fallback, fixed });
                return newSlots;
            });
        },
        [setFloatingVideoSlots],
    );

    const removeFloatingVideoSlot = useCallback(
        (id = null) => {
            setFloatingVideoSlots((slots) => [...slots].filter(({ id: slotId }) => slotId !== id));
        },
        [setFloatingVideoSlots],
    );

    const updateFloatingVideoSlot = useCallback(
        (id, slot) => {
            setFloatingVideoSlots((slots) =>
                slots.map(({ id: slotId, ...others }) =>
                    id === slotId ? { ...others, ...slot, id } : { ...others, id: slotId },
                ),
            );
        },
        [setFloatingVideoSlots],
    );

    const disableVideoSlotByName = useCallback(
        (name) => {
            setFloatingVideoSlots((slots) =>
                slots.map(({ name: slotName, ...others }) =>
                    name === slotName
                        ? { ...others, disabled: true, name: slotName }
                        : { ...others, name: slotName },
                ),
            );
        },
        [setFloatingVideoSlots],
    );

    const enableVideoSlotByName = useCallback(
        (name) => {
            setFloatingVideoSlots((slots) =>
                slots.map(({ id: slotName, ...others }) =>
                    name === slotName
                        ? { ...others, disabled: false, name }
                        : { ...others, name: slotName },
                ),
            );
        },
        [setFloatingVideoSlots],
    );

    const currentMaxVideoWidth = useMemo(
        () =>
            floatingVideoSlots.reduce((max, item) => {
                const { rect = null } = item || {};
                const { width = 0 } = rect || {};
                if (width > 0 && width >= max) {
                    return width;
                }
                return max;
            }, 0),
        [floatingVideoSlots],
    );

    // Initial value on first load
    useEffect(() => {
        if (currentMaxVideoWidth > 0 && maxVideoWidth === null) {
            setMaxVideoWidth(currentMaxVideoWidth);
        }
    }, [currentMaxVideoWidth, setMaxVideoWidth]);

    // Update value after a delay to prevent... well i dunno
    useEffect(() => {
        let id = null;
        if (currentMaxVideoWidth > 0 && maxVideoWidth !== null) {
            id = setTimeout(() => {
                setMaxVideoWidth(currentMaxVideoWidth);
            }, 500);
        }
        return () => {
            clearTimeout(id);
        };
    }, [currentMaxVideoWidth, setMaxVideoWidth]);

    const fixedSlot = useMemo(
        () => floatingVideoSlots.find(({ fixed = false }) => fixed),
        [floatingVideoSlots],
    );
    const { id: fixedSlotId = null } = fixedSlot || {};

    const disableFixedSlot = useCallback(() => {
        updateFloatingVideoSlot(fixedSlotId, { disabled: true });
    }, [fixedSlotId]);

    const enableFixedSlot = useCallback(() => {
        updateFloatingVideoSlot(fixedSlotId, { disabled: false });
    }, [fixedSlotId]);

    useEffect(() => {
        if (windowWidth < 768 && !fullscreen) {
            disableFixedSlot();
        }
    }, [fullscreen, windowWidth]);

    const value = useMemo(
        () => ({
            floatingVideo,
            setFloatingVideo,
            videoMetadata,
            floatingVideoSlots,
            addFloatingVideoSlot,
            removeFloatingVideoSlot,
            updateFloatingVideoSlot,
            disableVideoSlotByName,
            enableVideoSlotByName,
            disableFixedSlot,
            enableFixedSlot,
            fixedSlot,
            maxVideoWidth,
            fullscreen,
            setFullscreen,
        }),
        [
            floatingVideo,
            setFloatingVideo,
            videoMetadata,
            floatingVideoSlots,
            addFloatingVideoSlot,
            removeFloatingVideoSlot,
            updateFloatingVideoSlot,
            disableVideoSlotByName,
            enableVideoSlotByName,
            disableFixedSlot,
            enableFixedSlot,
            fixedSlot,
            maxVideoWidth,
            fullscreen,
            setFullscreen,
        ],
    );

    return <VideoContext.Provider value={value}>{children}</VideoContext.Provider>;
};

VideoProvider.propTypes = propTypes;
VideoProvider.defaultProps = defaultProps;

export default VideoContext;
